/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Unplug, Plug, Loader2, Info } from 'lucide-react';
import { providerIcon } from './metrics';

const StatusBadge = ({ connected, supported }) => {
  if (connected) return <Badge variant="outline" className="text-[10px] border-emerald-300 bg-emerald-50 text-emerald-800">Collegato</Badge>;
  if (supported) return <Badge variant="outline" className="text-[10px]">Disponibile</Badge>;
  return <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-800">In arrivo</Badge>;
};

const ProviderCard = ({ p, busy, onConnect, onDisconnect }) => {
  const PIcon = providerIcon(p.id);
  const isConnected = p.connected;
  return (
    <Card className={isConnected ? 'border-primary/40 bg-primary/5' : ''} data-testid={`iot-provider-${p.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${isConnected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            <PIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm">{p.name}</CardTitle>
            <div className="mt-0.5"><StatusBadge connected={isConnected} supported={p.supported} /></div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground min-h-[36px]">{p.description}</p>
        <p className="text-[11px] text-muted-foreground italic">{p.status_message}</p>
        {isConnected ? (
          <Button variant="outline" size="sm" className="w-full" onClick={() => onDisconnect(p)} disabled={busy} data-testid={`iot-disconnect-${p.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />}
            Scollega
          </Button>
        ) : (
          <Button size="sm" className="w-full" onClick={() => onConnect(p)} disabled={busy} data-testid={`iot-connect-${p.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
            {p.supported ? 'Collega' : 'Attiva (notifica quando disponibile)'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

/** Sezione "Sorgenti dati": provider IoT collegabili + nota sulle integrazioni native. */
export const ProvidersSection = ({ providers, loading, busy, onConnect, onDisconnect }) => (
  <section className="space-y-3" data-testid="iot-providers-section">
    <h2 className="font-display text-lg font-semibold">Sorgenti dati</h2>
    {loading && !providers ? (
      <div className="grid gap-3 sm:grid-cols-3">
        <Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" />
      </div>
    ) : (
      <div className="grid gap-3 sm:grid-cols-3">
        {(providers || []).map((p) => (
          <ProviderCard key={p.id} p={p} busy={!!busy[p.id]} onConnect={onConnect} onDisconnect={onDisconnect} />
        ))}
      </div>
    )}
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 flex items-start gap-2">
      <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
      <div className="text-xs text-muted-foreground">
        Le integrazioni native <b>Apple Health</b> e <b>Google Fit</b> saranno disponibili con la prossima release. Nel frattempo puoi inserire manualmente i tuoi valori: verranno mostrati anche al medico durante le visite.
      </div>
    </div>
  </section>
);
